import { createAsyncThunk } from "@reduxjs/toolkit";
import apiConfig from "constants/apiConfig";
import { bookmarkApplciation as bookmarkApplciationAPI, fetchApplication as fetchApplicationAPI, fetchApplicationMedia, fetchCampaignApplications as fetchCampaignApplicationsAPI } from "services/api/campaign/application";
import { AuthState } from "slices/auth/reducer";
import { ERROR } from "types";
import { ApplicationPosts, CampaignApplication, CampaignApplications, CampaignApplicationSlice } from "types/campaign/applications";
import { clearShortlistExit, pulseShortlistTab, startShortlistExit } from "slices/ui/reducer";
import { generateUniqueKey, getErrorMessage, isFresh } from "utils/helpers";

const SHORTLIST_EXIT_ANIMATION_MS = 480;

const delay = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

const dedupeApplicationsById = (items: CampaignApplication[]): CampaignApplication[] => {
    const seen = new Set<string>();
    return items.filter((item) => {
        if (seen.has(item.uuid)) return false;
        seen.add(item.uuid);
        return true;
    });
};

export const revalidateLoadedApplicationPages = createAsyncThunk<
    {
        campaignApplications: CampaignApplications;
        list: CampaignApplication[];
        filters: { page: number; [key: string]: any };
        filterId: string;
        campaignId: string;
        maxPage: number;
    },
    {
        campaignId: string;
        filters: { page: number; [key: string]: any };
        maxPage: number;
        filterId: string;
    },
    { rejectValue: string }
>(
    'campaign/applications/revalidate',
    async ({ campaignId, filters, maxPage, filterId }, { getState, rejectWithValue }) => {
        try {
            const state = getState() as { auth: AuthState };
            const agencyId = state.auth.selectedAgency?.uuid ?? '';
            if (!agencyId) throw new Error('No agency selected!');

            let merged: CampaignApplication[] = [];
            let lastResponse: CampaignApplications | null = null;

            for (let page = 1; page <= maxPage; page++) {
                const response = await fetchCampaignApplicationsAPI(agencyId, campaignId, {
                    ...filters,
                    page,
                });
                lastResponse = response;
                merged = [...merged, ...response.items];
            }

            if (!lastResponse) {
                throw new Error('Failed to revalidate application pages');
            }

            const list = dedupeApplicationsById(merged);

            return {
                campaignApplications: lastResponse,
                list,
                filters: { ...filters, page: maxPage },
                filterId,
                campaignId,
                maxPage,
            };
        } catch (error) {
            return rejectWithValue(getErrorMessage(error, 'Failed to revalidate applications'));
        }
    }
);

export const fetchCampaignApplications = createAsyncThunk<
    { campaignApplications: CampaignApplications; filters: { page: number; [key: string]: any }; filterId: string; campaignId: string },
    { campaignId: string; force?: boolean; silent?: boolean; filters: { page: number; [key: string]: any } },
    { rejectValue: string }
>(
    'campaign/applications',
    async ({ campaignId, force, filters }, { getState, rejectWithValue }) => {
        try {
            const STALE_TIME = apiConfig.FETCH_CAMPAIGN_APPLICATIONS.staleTime;
            const filterId = generateUniqueKey(filters, ['page']);
            const state = getState() as { auth: AuthState, campaignApplications: CampaignApplicationSlice };
            const cachedList = state.campaignApplications.campaigns[campaignId]?.filtered_lists?.[filterId];
            if (
                filters.page === 1 &&
                !force &&
                cachedList &&
                !cachedList.expired &&
                isFresh(cachedList.last_fetched_at, STALE_TIME)
            ) {
                return rejectWithValue(ERROR.CACHED);
            }
            const agencyId = state.auth.selectedAgency?.uuid ?? '';
            const campaignApplications = await fetchCampaignApplicationsAPI(agencyId, campaignId, filters);
            return { campaignApplications, filters, filterId, campaignId };
        } catch (error) {
            return rejectWithValue('Failed to fetch campaign applications');
        }
    }
)

export const fetchApplication = createAsyncThunk<{ application: CampaignApplication, applicationId: string, campaignId: string }, { campaignId: string, applicationId: string, force?: boolean }, { rejectValue: string }>(
    'campaign/application',
    async ({ campaignId, applicationId, force }, { getState, rejectWithValue }) => {
        try {
            const STALE_TIME = apiConfig.FETCH_APPLICATION.staleTime;
            const state = getState() as { auth: AuthState, campaignApplications: CampaignApplicationSlice };

            if (!force && isFresh(state.campaignApplications.campaigns[campaignId]?.applications?.[applicationId]?.last_fetched_at ?? null, STALE_TIME)) {
                rejectWithValue(ERROR.CACHED);
            }
            const agencyId = state.auth.selectedAgency?.uuid ?? '';
            const application = await fetchApplicationAPI(agencyId, campaignId, applicationId);
            return { application, campaignId, applicationId };

        } catch (error) {
            return rejectWithValue(getErrorMessage(error, 'Failed to fetch application'));
        }
    }
)

export const bookmarkApplciation = createAsyncThunk<
    { campaignId: string; applicationId: string; bookmarked: 'y' | 'n'; force?: boolean },
    {
        campaignId: string;
        applicationId: string;
        bookmark: 'y' | 'n';
        force?: boolean;
        animateShortlist?: boolean;
    },
    { rejectValue: {errorCode: string, message: string} }
>(
    'campaign/bookmark',
    async (
        { campaignId, applicationId, bookmark, force = false, animateShortlist = false },
        { getState, dispatch, rejectWithValue }
    ) => {
        const shouldAnimate = animateShortlist && bookmark === 'y';

        try {
            const state = getState() as { auth: AuthState };

            const agencyId = state.auth.selectedAgency?.uuid;
            if (!agencyId) throw new Error('No agency selected!');

            const status = await bookmarkApplciationAPI(agencyId, campaignId, applicationId, bookmark, force);

            if (!status) throw new Error('Failed to bookmark application');

            if (shouldAnimate) {
                dispatch(startShortlistExit({ campaignId, applicationId }));
                dispatch(pulseShortlistTab());
                await delay(SHORTLIST_EXIT_ANIMATION_MS);
                dispatch(clearShortlistExit());
            }

            return { campaignId, applicationId, bookmarked: bookmark };
        } catch (error: any) {
            dispatch(clearShortlistExit());
            const errorCode = error?.response?.data?.error_code;
            return rejectWithValue({ errorCode: errorCode ?? 'unknown_error', message: error?.response?.data?.message ?? 'Failed to shortlist this application' });
        }
    }
);

export const fetchApplicationPosts = createAsyncThunk<{ posts: ApplicationPosts, influencerId: string, campaignId: string }, { campaignId: string, influencerId: string, force?: boolean }, { rejectValue: string }>(
    'campaign/application/post',
    async ({ campaignId, influencerId, force }, { getState, rejectWithValue }) => {
        try {
            const STALE_TIME = apiConfig.FETCH_APPLICATION_POSTS.staleTime;
            const state = getState() as { auth: AuthState, campaignApplications: CampaignApplicationSlice };

            if (!force && isFresh(state.campaignApplications.posts[campaignId]?.[influencerId]?.last_fetched_at ?? null, STALE_TIME)) {
                rejectWithValue(ERROR.CACHED);
            }
            const agencyId = state.auth.selectedAgency?.uuid ?? '';
            const posts = await fetchApplicationMedia(agencyId, campaignId, influencerId);
            return { posts, campaignId, influencerId };

        } catch (error) {
            return rejectWithValue(getErrorMessage(error, 'Failed to fetch application posts'));
        }
    }
)